import * as React from 'react'
import Spinner from 'components/loaders/Spinner/Spinner'
import { AppContext } from 'context/appContext'
import {
  DOWNLOAD_MANAGER_COMPLETED_STATUS,
  DOWNLOAD_MANAGER_PENDING_STATUS,
  DOWNLOAD_MANAGER_POLLING_DELAY,
  DOWNLOAD_MANAGER_READY_STATUS,
  DOWNLOAD_MANAGER_REQUESTED_STATUS,
  DOWNLOAD_SERVICE_LEGACY,
  DOWNLOAD_SERVICE_V2
} from 'shared/context/DownloadManagerContext/constants'
import { downloadFromUrl } from 'utils/files'
import { DownloadManagerContext } from 'shared/context/DownloadManagerContext'
import { getUserSessionDataFromLocalStorage } from 'utils/auth'
import { IDownload } from 'shared/context/DownloadManagerContext/interface'
import { IHTTPVerbsData } from 'lib/interface'
import { IReport } from './interface'
import { listReportsGet } from 'lib/reportApi'
import { showToastAlert } from 'utils/helpers'
import { useTranslation } from 'react-i18next'
import './DownloadManager.scss'

const { useContext, useEffect, useRef, useState } = React
const t_root = 'downloadsManager'
const t_root_toast = `${t_root}.toast`

const DownloadManager = (): JSX.Element => {
  const { t } = useTranslation()
  const {
    company: { entityId, testMode }
  } = useContext(AppContext)
  const {
    deactivateService,
    downloads,
    findDownload,
    isActiveService,
    managerActive,
    pendingDownloads,
    readyDownloads,
    updateDownload,
    updateDownloads,
    updateManagerActive
  } = useContext(DownloadManagerContext)
  const [inProcessDownloads, setInProcessDownloads] = useState<IDownload[]>([])
  const [active, setActive] = useState(false)
  const [show, setShow] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [polling, setPolling] = useState(false)
  const session_id: string = getUserSessionDataFromLocalStorage()?.profile?.sid
  const polling_timer = useRef(0)
  const title_label = t(`${t_root}.title`)

  useEffect(() => {
    if (managerActive) {
      startManager()

      if (!active) {
        schedulePollingDownloads()
      }

      updateManagerActive(false)
    }
  }, [managerActive])

  useEffect(() => {
    if (polling) {
      getCurrentDownloads().then(current_downloads => {
        updateDownloads(current_downloads)
      })
      setPolling(false)
    }
  }, [polling])

  useEffect(() => {
    if (active) {
      const pending_downloads = pendingDownloads()
      const ready_downloads = readyDownloads()
      const has_pending_downloads = pending_downloads.length > 0
      const has_ready_downloads = ready_downloads.length > 0

      setInProcessDownloads(pending_downloads)

      if (has_ready_downloads) {
        requestDownloads(ready_downloads)
      }

      if (!has_pending_downloads) {
        stopManager()
      }
    }
  }, [downloads])

  const getCurrentDownloads = async (): Promise<IDownload[]> => {
    let current_downloads: IDownload[] = []

    const payload: IHTTPVerbsData = { sessionId: session_id, testMode }
    const reports_downloads_v1 = await getReportDownloads(payload, DOWNLOAD_SERVICE_LEGACY)
    const reports_downloads_v2 = await getReportDownloads(payload, DOWNLOAD_SERVICE_V2)
    const new_downloads = [...reports_downloads_v1, ...reports_downloads_v2]

    current_downloads = getUpdatedDownloads(new_downloads)

    return current_downloads
  }

  const getReportDownloads = async (payload: IHTTPVerbsData, service: string): Promise<IDownload[]> => {
    let report_downloads: IDownload[] = []

    if (isActiveService(service)) {
      return listReportsGet({ ...payload, version: service })
        .then(({ data = { reports: [] } }) => {
          const { reports = [] } = data

          report_downloads = reports.map(({ report_id, report_type, status, url }: IReport) => ({
            id: report_id,
            type: report_type,
            url: url.trim(),
            status,
            service
          }))

          return report_downloads
        })
        .catch(() => {
          showToastAlert(t, 'error', t_root_toast)

          return report_downloads
        })
        .finally(() => {
          const pending_reports = report_downloads.filter(({ url }: IDownload) => url === '')
          const has_pending_reports = pending_reports.length === 0

          if (has_pending_reports) {
            deactivateService(service)
          }
        })
    } else {
      return Promise.resolve(report_downloads)
    }
  }

  const getUpdatedDownloads = (newDownloads: IDownload[]): IDownload[] => {
    const new_downloads = newDownloads.map((new_download: IDownload) => {
      const old_download = findDownload(new_download.id)
      const { url = '' } = new_download
      let current_download = { ...new_download, status: getDownloadStatus(DOWNLOAD_MANAGER_PENDING_STATUS, url) }

      if (old_download) {
        const status = new_download.status === 'error' ? 'error' : old_download.status
        current_download = { ...old_download, url, status: getDownloadStatus(status, url) }
      }

      if (current_download.status === 'error' && old_download?.status != 'error') {
        showToastAlert(t, 'error', t_root_toast)
      }

      return current_download
    })
    const ids_to_exclude = new_downloads.map(({ id }: IDownload) => id)
    const old_downloads = downloads.filter(({ id }: IDownload) => !ids_to_exclude.includes(id))
    const updated_downloads = [...old_downloads, ...new_downloads]

    return updated_downloads
  }

  const getDownloadStatus = (status: string, url: string): string => {
    if (status === DOWNLOAD_MANAGER_PENDING_STATUS && url !== '') {
      return DOWNLOAD_MANAGER_READY_STATUS
    } else if (status === DOWNLOAD_MANAGER_REQUESTED_STATUS) {
      return DOWNLOAD_MANAGER_COMPLETED_STATUS
    } else {
      return status
    }
  }

  const schedulePollingDownloads = (): void => {
    polling_timer.current = window.setInterval(() => {
      setPolling(true)
    }, DOWNLOAD_MANAGER_POLLING_DELAY)
  }

  const stopPollingDownloads = (): void => {
    window.clearInterval(polling_timer.current)
    polling_timer.current = 0
    setPolling(false)
  }

  const requestDownloads = (downloadsList: IDownload[]): void => {
    downloadsList.map((download: IDownload) => {
      const { url = '' } = download

      updateDownload({ ...download, status: DOWNLOAD_MANAGER_REQUESTED_STATUS })
      downloadFromUrl(url)
    })

    showToastAlert(t, 'success', t_root_toast)
  }

  const startManager = (): void => {
    setActive(true)
    setShow(true)
    setPolling(true)
  }

  const stopManager = (): void => {
    setActive(false)
    setShow(false)
    stopPollingDownloads()
  }

  if (!entityId) {
    return <></>
  }

  return (
    <>
      {show && (
        <div className='Handler' draggable='true'>
          <div className='Handler__title'>
            <p>{title_label}</p>
            <div className='Handler__buttons'>
              <button className='Handler__item-close' onClick={(): void => setMinimized(!minimized)}>
                <i className={minimized ? 'icon-caret-up' : 'icon-caret-down'} />
              </button>
              <button className='Handler__item-close' onClick={(): void => setShow(false)}>
                <i className='icon-close' />
              </button>
            </div>
          </div>
          {active &&
            !minimized &&
            inProcessDownloads.map((report: IDownload) => {
              const { id, type } = report
              const description_label = `${t(`${t_root}.description`)} - ${t(`${t_root}.type.${type}`)}`

              return (
                <div className='Handler__content' key={id}>
                  <div className='Handler__items'>
                    <p>{description_label}</p>
                    <Spinner wrapper={'gestor'} show />
                  </div>
                </div>
              )
            })}
        </div>
      )}
    </>
  )
}

export default DownloadManager
